/**
 * Constantes compartilhadas entre servidor, engine e frontend.
 * O nome da aplicação é configurável por variável de ambiente no frontend
 * (VITE_APP_NAME) e cai para este valor padrão.
 */

export const APP_NAME = 'Sincronia';
export const APP_VERSION = '0.1.0';

/** Baralho: cartas numeradas de 1 a 100, sem duplicatas dentro do nível. */
export const DECK_MIN_VALUE = 1;
export const DECK_MAX_VALUE = 100;
export const DECK_SIZE = DECK_MAX_VALUE - DECK_MIN_VALUE + 1;

export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 8;

export const MAX_LIVES = 5;
export const MIN_LIVES = 1;
export const MAX_STARS = 3;
export const MIN_STARS = 0;
export const STARTING_STARS = 1;

export const NICKNAME_MIN_LENGTH = 2;
export const NICKNAME_MAX_LENGTH = 20;

export const ROOM_CODE_LENGTH = 6;
/** Alfabeto sem caracteres ambíguos: sem 0, O, 1, I e L. */
export const ROOM_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

/** Duração da contagem regressiva sincronizada (3, 2, 1). */
export const COUNTDOWN_MS = 3000;
/** Tempo de exibição da animação de distribuição de cartas. */
export const DEALING_MS = 900;
/** Prazo da votação de estrela. */
export const STAR_VOTE_TIMEOUT_MS = 15_000;
/** Tempo em que a tela de erro fica visível antes da recentralização. */
export const ERROR_REVEAL_MS = 4000;
/**
 * Prazo máximo dos descartes da estrela. Normalmente a tela sai quando todos
 * confirmam que viram; este limite existe só para a sala não travar se alguém
 * parar de responder.
 */
export const STAR_ACK_TIMEOUT_MS = 45_000;
/** Janela mínima de reconexão antes de o anfitrião poder encerrar a partida. */
export const RECONNECT_GRACE_MS = 90_000;

/** TTL padrão de salas inativas (6 horas). */
export const DEFAULT_ROOM_TTL_SECONDS = 6 * 60 * 60;

/** Frequência de envio das posições de arrasto pela rede. */
export const DRAG_SEND_HZ = 15;
export const DRAG_SEND_INTERVAL_MS = Math.round(1000 / DRAG_SEND_HZ);
/** Limite defensivo aplicado no servidor (eventos por segundo, por jogador). */
export const DRAG_RATE_LIMIT_PER_SECOND = 30;

/** Sincronização de relógio. */
export const CLOCK_SYNC_SAMPLES = 5;
export const CLOCK_SYNC_INTERVAL_MS = 30_000;

/** Chaves de avatar/cor — a ordem define a atribuição por assento. */
export const AVATAR_KEYS = [
  'aurora',
  'citrino',
  'coral',
  'jade',
  'lilas',
  'oceano',
  'brasa',
  'neve',
] as const;

export type AvatarKey = (typeof AVATAR_KEYS)[number];

/**
 * Presets calculados com o número real de jogadores conectados no início.
 */
export const PLAYER_PRESETS: Record<number, { maxLevel: number; startingLives: number }> = {
  2: { maxLevel: 12, startingLives: 2 },
  3: { maxLevel: 10, startingLives: 3 },
  4: { maxLevel: 8, startingLives: 4 },
  5: { maxLevel: 6, startingLives: 5 },
  6: { maxLevel: 5, startingLives: 5 },
  7: { maxLevel: 4, startingLives: 5 },
  8: { maxLevel: 4, startingLives: 5 },
};

/** Recompensa concedida ao concluir o nível indicado. */
export const REWARD_BY_LEVEL: Record<number, 'star' | 'life'> = {
  2: 'star',
  3: 'life',
  5: 'star',
  6: 'life',
  8: 'star',
  9: 'life',
};

export function presetForPlayers(playerCount: number): { maxLevel: number; startingLives: number } {
  const clamped = Math.min(MAX_PLAYERS, Math.max(MIN_PLAYERS, playerCount));
  return PLAYER_PRESETS[clamped] ?? { maxLevel: 4, startingLives: 5 };
}
